import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';

import { api } from '@/lib/axios';
import { getUserLocale } from '@/utils/locale';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useTranslation } from '@/i18n';

import { PageContainer } from '../components/layout/PageContainer';
import { Logo } from '../components/common/Logo';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { TermsDialog } from '../components/common/TermsDialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../components/ui/form';

// Función para corregir emails automáticamente
const correctEmail = (email: string): { corrected: string; wasCorrected: boolean } => {
  const domains = ['gmail', 'hotmail', 'outlook', 'yahoo'];
  const lowerEmail = email.toLowerCase();
  
  for (const domain of domains) {
    if (lowerEmail.endsWith(`@${domain}`)) {
      return {
        corrected: `${email}.com`,
        wasCorrected: true
      };
    }
  }
  
  return { corrected: email, wasCorrected: false };
};



interface Restaurant {
  name: string;
  logoUrl: string;
}

export function IdentifyPage() {
  const { providerOrderId } = useParams<{ providerOrderId: string }>();
  const navigate = useNavigate();
  const { trackIdentify } = useAnalytics();

  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [isLoadingOrder, setIsLoadingOrder] = useState(true);
  const [termsOpen, setTermsOpen] = useState(false);

  const { t, locale } = useTranslation();

  // Zod validation schema with translated messages
  const identifySchema = z.object({
    name: z
      .string()
      .min(2, t('valNameMin'))
      .max(100, t('valNameMax')),
    email: z
      .string()
      .refine((val) => {
        const emailRegex = /^[^\s@]+@[^\s@]+(\.[^\s@]+)?$/;
        return emailRegex.test(val);
      }, t('valEmailInvalid'))
      .max(255, t('valEmailMax')),
    acceptTerms: z
      .boolean()
      .refine((val) => val === true, {
        message: t('valMustAccept'),
      }),
  });
  type IdentifyFormData = z.infer<typeof identifySchema>;

  const form = useForm<IdentifyFormData>({
    resolver: zodResolver(identifySchema),
    defaultValues: {
      name: '',
      email: '',
      acceptTerms: false,
    },
  });

  // Cargar orden al montar el componente
  useEffect(() => {
    async function loadOrder() {
      if (!providerOrderId) {
        toast.error(t('invalidOrderId'));
        setIsLoadingOrder(false);
        return;
      }

      try {
        const response = await api.get(`/v1/orders/${providerOrderId}`);
        setRestaurant(response.data.restaurant);
        setIsLoadingOrder(false);
        
        // Track: user started identify flow
        trackIdentify.start(providerOrderId, response.data.restaurant?.name);
      } catch (error) {
        console.error('Error loading order:', error);
        toast.error(t('orderLoadError'));
        setIsLoadingOrder(false);
      }
    }

    loadOrder();
  }, [providerOrderId, trackIdentify, t]);

  const onSubmit = async (data: IdentifyFormData) => {
    if (!providerOrderId) {
      toast.error(t('invalidOrderId'));
      return;
    }

    // Aplicar corrección automática de email
    const { corrected: correctedEmail, wasCorrected } = correctEmail(data.email);
    
    if (wasCorrected) {
      toast.info(t('emailCorrected', { email: correctedEmail }));
    }

    try {
      // Asociar usuario a memoria existente (o crear si no existe)
      const response = await api.post('/v1/memories', {
        providerOrderId,
        user: {
          name: data.name,
          email: correctedEmail,
          locale: getUserLocale(),
        },
      });

      // Track: successful form submission
      trackIdentify.submit(providerOrderId, restaurant?.name);

      // Navegar a MemoryPage donde verá progreso y video
      toast.success(t('welcome'));
      navigate(`/memory/${response.data.slug}`);
    } catch (error) {
      console.error('Error creating memory:', error);
      
      // Track: form submission error
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      trackIdentify.error(providerOrderId, errorMessage);
      
      toast.error(t('memoryError'));
    }
  };

  if (isLoadingOrder) {
    return (
      <PageContainer className="flex items-center justify-center">
        <div className="text-center space-y-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-content-primary mx-auto"></div>
          <p className="font-serif text-sm text-content-secondary font-light">{t('loading')}</p>
        </div>
      </PageContainer>
    );
  }

  if (!restaurant) {
    return (
      <PageContainer className="flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="font-serif text-base text-content-primary font-light">{t('orderNotFound')}</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="flex flex-col min-h-screen p-0">
      {/* Main content - scrollable area, centered vertically */}
      <div className="flex flex-col justify-center items-center min-h-[calc(100vh-100px)] px-4 py-8 overflow-y-auto">
        <div className="w-full max-w-md space-y-8">
          {/* Logo - Top section */}
          <div className="flex justify-center">
            <Logo
              src={restaurant.logoUrl}
              alt={restaurant.name}
              className="h-10"
            />
          </div>

          {/* Intro text */}
          <div>
            <p className="font-serif text-[13px] leading-[20px] text-content-secondary font-light text-left">
              {t('introText', { name: restaurant.name })}
            </p>
          </div>

          {/* Form */}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Name field */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <div className="relative">
                      <FormControl>
                        <input
                          {...field}
                          placeholder={t('namePlaceholder')}
                          className="w-full bg-transparent border-0 border-b border-gray-300 text-[14px] leading-[14px] font-light focus:outline-none focus:border-content-primary transition-colors pb-2 peer placeholder:text-gray-400 placeholder:text-[14px]"
                        />
                      </FormControl>
                      <FormLabel className="absolute left-0 top-2 text-[12px] text-content-primary font-light pointer-events-none transition-all opacity-0 peer-focus:opacity-100 peer-focus:top-[-20px] peer-[:not(:placeholder-shown)]:opacity-100 peer-[:not(:placeholder-shown)]:top-[-20px]">
                        {t('nameLabel')}
                      </FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Email field */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <div className="relative">
                      <FormControl>
                        <input
                          {...field}
                          type="email"
                          placeholder={t('emailPlaceholder')}
                          className="w-full bg-transparent border-0 border-b border-gray-300 text-[14px] leading-[14px] font-light focus:outline-none focus:border-content-primary transition-colors pb-2 peer placeholder:text-gray-400 placeholder:text-[14px]"
                        />
                      </FormControl>
                      <FormLabel className="absolute left-0 top-2 text-[12px] text-content-primary font-light pointer-events-none transition-all opacity-0 peer-focus:opacity-100 peer-focus:top-[-20px] peer-[:not(:placeholder-shown)]:opacity-100 peer-[:not(:placeholder-shown)]:top-[-20px]">
                        {t('emailLabel')}
                      </FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Spacing before checkbox */}
              <div className="pt-4" />

              {/* Terms checkbox */}
              <FormField
                control={form.control}
                name="acceptTerms"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-[14px] font-light text-content-primary cursor-pointer">
                        {t('acceptTermsPrefix')}
                        <button
                          type="button"
                          onClick={() => setTermsOpen(true)}
                          className="underline decoration-solid cursor-pointer hover:opacity-70 transition-opacity"
                        >
                          {t('termsLink')}
                        </button>
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>
      </div>

      {/* Submit button - Fixed at bottom, centered on desktop */}
      <div className="fixed bottom-0 left-0 right-0 px-4 pb-6 pt-4 bg-white border-t border-gray-100">
        <div className="max-w-md mx-auto">
          <Button
          onClick={() => form.handleSubmit(onSubmit)()}
          className="w-full h-10 bg-content-primary text-content-contrast font-light text-base tracking-wide"
          disabled={form.formState.isSubmitting}
        >
          {form.formState.isSubmitting ? t('submitting') : t('submit')}
        </Button>
        </div>
      </div>

      <TermsDialog open={termsOpen} onOpenChange={setTermsOpen} locale={locale} />
    </PageContainer>
  );
}
